import { mapGetters, mapActions } from 'vuex';
import { getStateInfo } from '@utils/get-state';
import { authComputed } from '@state/helpers';
import { trackDisableAutoAccept, trackExtendDecisionPeriod, trackRankingUpdated } from '@utils/analytics/events';
import StateEnum from '@enums/state-enum';
import { getRankingPreferences } from '@enums/preferences/ranking';
import { MediaMixin } from '@loandolphin/dolphin-framework';

const SETTINGS = { OFF: 'off', ON: 'on' };

export default {
  mixins: [MediaMixin],
  name: 'enquiry-settings',
  props: {
    uuid: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isDecisionPeriodLoading: false,
      toggleOptions: [
        { key: SETTINGS.OFF, value: this.$t('dashboard.settings.label.off') },
        { key: SETTINGS.ON, value: this.$t('dashboard.settings.label.on') },
      ],
      selectedRankingOption: null,
    };
  },

  computed: {
    ...authComputed,

    ...mapGetters({
      getEnquiryByUuid: 'connector/getEnquiryByUuid',
    }),

    enquiry() {
      return this.getEnquiryByUuid(this.uuid);
    },

    autoAccept() {
      return this.enquiry.auto_accept;
    },

    extendedExpirationDays() {
      return this.enquiry.extended_expiration_days;
    },

    decisionPeriodExpirydDate() {
      return this.enquiry.expiration_date;
    },

    state() {
      return this.stateInfo.state;
    },

    stateInfo() {
      return getStateInfo(this.enquiry);
    },

    showAutoAccept() {
      return (this.state === StateEnum.DECISION)
        || this.state === StateEnum.BIDDING;
    },

    showExtendPeriod() {
      return this.state === StateEnum.DECISION;
    },

    extensionsUsed() {
      if (this.extendedExpirationDays === 0) {
        return 0;
      }

      if (this.extendedExpirationDays === 2) {
        return 1;
      }

      return 2;
    },

    autoAcceptValue: {
      get() {
        return this.autoAccept ? SETTINGS.ON : SETTINGS.OFF;
      },
      set(value) {
        if (value !== SETTINGS.OFF) {
          return;
        }

        this.disableAutoAccept();
      },
    },

    isAutoAcceptDisabled() {
      return !this.autoAccept;
    },

    isExtendPeriodDisabled() {
      return this.extendedExpirationDays > 3;
    },

    rankingOption: {
      get() {
        if (this.selectedRankingOption) {
          return { ...this.selectedRankingOption, value: this.$t(`enquiry.preference.ranking_order.${this.selectedRankingOption.key}`) };
        }

        if (this.enquiry.sort_order) {
          return this.rankingOptions.find((option) => option.key === this.enquiry.sort_order);
        }

        return this.rankingOptions[1];
      },

      set(newValue) {
        this.selectedRankingOption = newValue;
      },
    },

    rankingOptions() {
      return getRankingPreferences().map((rankingOption) => ({
        key: rankingOption,
        value: this.$t(`enquiry.preference.ranking_order.${rankingOption}`),
      }));
    },
  },

  methods: {
    ...mapActions({
      cancelAutoAccept: 'connector/cancelAutomaticSelection',
      extendReviewPeriod: 'connector/extendReviewPeriod',
      listEnquiries: 'connector/listEnquiries',
      updateSortingPreference: 'connector/updateSortingPreference',
    }),

    disableAutoAccept() {
      return this.cancelAutoAccept(this.enquiry)
        .then(() => {
          trackDisableAutoAccept(this.currentUser, this.enquiry);
          this.listEnquiries();
        });
    },

    extendDecisionPeriod() {
      if (this.isExtendPeriodDisabled) {
        return false;
      }

      this.isDecisionPeriodLoading = true;


      return this.extendReviewPeriod(this.enquiry)
        .then(async () => {
          await this.listEnquiries();
        })
        .then(() => {
          this.isDecisionPeriodLoading = false;
          trackExtendDecisionPeriod(this.currentUser, this.enquiry);
        });
    },

    onSelectRankingOption() {
      return this.updateSortingPreference({
        enquiry: this.enquiry,
        sortBy: this.selectedRankingOption.key,
      })
        .then(() => this.listEnquiries())
        .then(() => {
          trackRankingUpdated(
            this.currentUser,
            this.enquiry,
            this.selectedRankingOption.value,
            this.state.value,
          );
        });
    },
  },
};
